import { i18nNsFactory } from '@atlas-frontend/i18n';
import {
  BookmarkCreate,
  ButtonGroupNav,
  type DataObjectGroupsWithDescriptionsMapById,
  getDayjs,
  getGroupDescriptionRef,
  InfoIcon,
  InfoIconUrl,
} from '@atlas-frontend/sdk';
import { Flex } from 'antd';
import styled from 'styled-components';
import { path } from '../../paths';
import { withParams } from '../../paths/withParams';
import translations from './SampleHeader.translations.json';
import { GroupMemberSelect } from './GroupMemberSelect';
import { SampleSearch, SnapshotData } from './SampleSearch';
import { useMemo } from 'react';

const { addTranslations, useTranslation } = i18nNsFactory('sample-header');
addTranslations(translations);

export type HeaderType = 'input-data' | 'samples';

export type SampleHeaderProps = {
  data?: DataObjectGroupsWithDescriptionsMapById;
  date?: string;
  groupMemberId?: number;
  id?: number;
  isLoading?: boolean;
  sampleType?: 'description' | 'group';
  view: HeaderType;
  snapshotData?: SnapshotData;
};

export function SampleHeader({
  data,
  date,
  groupMemberId,
  id,
  isLoading,
  sampleType,
  view,
  snapshotData,
}: SampleHeaderProps) {
  const { t } = useTranslation();

  const groupId = useMemo(
    () =>
      sampleType === 'group'
        ? id
        : [...(data ?? []).values()]
            .flatMap((group) => group.descriptions)
            .find((description) => description.id === id)?.dataObjectGroupId,
    [sampleType, id, data],
  );
  const groupDescriptionRef = groupId
    ? getGroupDescriptionRef(
        groupId,
        sampleType === 'description' ? id : undefined,
      )
    : undefined;

  return (
    <Flex vertical gap="large" justify="space-between">
      {!id && (
        <Flex gap="small" align="start">
          <Title>{t('title')}</Title>
          <InfoIcon infoIconUrl={InfoIconUrl.SAMPLE_DIRECTORY}></InfoIcon>
          <BookmarkCreate />
        </Flex>
      )}
      <Flex justify="space-between" align="end">
        <SampleSearch
          isLoading={isLoading}
          initialValues={{
            groupDescriptionRef,
            date: getDayjs(date),
          }}
          treeData={data}
          uiPathName={view === 'samples' ? 'sampleView' : 'inputDataView'}
          snapshotData={snapshotData}
          groupMemberId={groupMemberId}
        />
        {date && (
          <Flex gap="middle">
            {sampleType === 'group' && <StyledGroupMemberSelect view={view} />}
            <Flex vertical>
              <Filler />
              <ButtonGroupNav
                items={[
                  {
                    key: 'samples',
                    label: t('segment-option.sample'),
                    to:
                      id && date && sampleType
                        ? withParams(path.sampleView, {
                            id,
                            date,
                            type: sampleType,
                          })
                        : '',
                    disabled: !id || !date,
                  },
                  {
                    key: 'input-data',
                    label: t('segment-option.data'),
                    to:
                      id && date
                        ? withParams(path.inputDataView, {
                            id,
                            date,
                            type: sampleType,
                          })
                        : '',
                    disabled: !id || !date,
                  },
                ]}
              />
            </Flex>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
}

const Filler = styled.div`
  flex: 1 1 auto;
`;

const StyledGroupMemberSelect = styled(GroupMemberSelect)`
  align-self: start;
  min-width: 170px;
`;

const Title = styled.h3`
  align-self: flex-start;
`;
